import { ServerConfig } from "@/config/config";
import { Dossier } from "@/models/dossier";
import { DossierPeppolID } from "@/models/dossier-peppol-id";
import { Module } from "@/models/module";
import { SaleType } from "@/models/sale-type";
import { User } from "@/models/user";
import { Axios } from "@/services/auth-service";
import { CompanyService } from "@/services/company-service";
import Vue from "vue";

export interface IMoveUserDataSetting{
	fromUserId:number
	toUserId:number
}

export const CompanyDossierService = new Vue({
	methods: {
		getUrl(companyId:number):string {
			return `${CompanyService.url}/${companyId}/dossiers`;
		},
		getMainAppUrl(dossierId:number):string{
			return `${ServerConfig.mainAppHost}/dossier/${dossierId}`;
		},
		async getDossiers(companyId:number):Promise<Dossier[]> {
			let result = await Axios.get(`${this.getUrl(companyId)}`);
			return result.data.map((d:any) => new Dossier(d));
		},
		async getDossier(companyId:number, dossierId:number):Promise<Dossier> {
			let result = await Axios.get(`${this.getUrl(companyId)}/${dossierId}`);
			return new Dossier(result.data);
		},
		async createDossier(companyId:number, dossier:Dossier):Promise<Dossier> {
			let result = await Axios.post(`${this.getUrl(companyId)}`, dossier.getJSON());
			let newDossier = new Dossier(result.data);
			this.initDatabase(newDossier.id);
			return newDossier;
		},
		async updateDossier(companyId:number, dossier:Dossier):Promise<Dossier> {
			let result = await Axios.put(`${this.getUrl(companyId)}/${dossier.id}`, dossier.getJSON());
			return new Dossier(result.data);
		},
		async deleteDossier(companyId:number, dossier:Dossier):Promise<void> {
			await Axios.delete(`${this.getUrl(companyId)}/${dossier.id}`);
		},
		async addUsers(dossier:Dossier, users:User[]):Promise<Dossier> {
			let result = await Axios.post(`${this.getUrl(dossier.companyId)}/${dossier.id}/users`, users.map(u => u.id));
			return new Dossier(result.data);
		},
		async removeUsers(dossier:Dossier, users:User[]):Promise<Dossier> {
			let result = await Axios.delete(`${this.getUrl(dossier.companyId)}/${dossier.id}/users`, {
				data: users.map(u => u.id)
			});
			return new Dossier(result.data);
		},
		async addModules(dossier:Dossier, modules:Module[]):Promise<Dossier> {
			let result = await Axios.post(`${this.getUrl(dossier.companyId)}/${dossier.id}/modules`, modules.map(m => m.id));
			return new Dossier(result.data);
		},
		async removeModules(dossier:Dossier, modules:Module[]):Promise<Dossier> {
			let result = await Axios.delete(`${this.getUrl(dossier.companyId)}/${dossier.id}/modules`, {
				data: modules.map(m => m.id)
			});
			return new Dossier(result.data);
		},
		async createWfOfflineToken(companyId:number, dossierId:number, userId:number):Promise<string>{
			let result = await Axios.get(`${this.getUrl(companyId)}/${dossierId}/wfoffline-access-token/${userId}`);
			return result.data.token;
		},
		async initDatabase(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/init-db`);
		},
		async fixPartitions(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/fix-partitions`);
		},
		async runMigration(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/migrate`);
		},
		async runSpecificMigration(dossierId:number, version:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/migrate/${version}`);
		},
		async recreateStockOuts(dossierId:number, startDate:Date | null, endDate:Date | null, startBookYear:number, endBookYear:number, createBackup:boolean):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/recreate-stock-outs`, {
				FromDate: startDate,
				TillDate: endDate,
				BeginBookYear: startBookYear,
				EndBookYear: endBookYear,
				CreateBackupFirst: createBackup
			});
		},
		async createMissingProductsFromSaleRows(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/create-missing-products-from-sale-rows`);
		},
		async createMissingExtraFields(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/create-missing-extra-field-records`);
		},
		async recomputeProductBuyPrices(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/recompute-product-buy-prices`);
		},
		async getSaleTypes(dossierId:number):Promise<SaleType[]>{
			let result = await Axios.get(`${this.getMainAppUrl(dossierId)}/sale-types`);
			return (result.data || []).map((d:any)=>new SaleType(d));
		},
		async getPeppolRegistrations(dossierId:number):Promise<DossierPeppolID[]>{
			let result = await Axios.get(`${this.getMainAppUrl(dossierId)}/peppol-registrations`);
			return ((result.data || []) as any[]).map(d=>new DossierPeppolID(d));
		},
		async registerPeppolEndpoint(dossierId:number, endpoint:string, sendOnly:boolean):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/register-in-peppol/${endpoint}`, {sendOnly});
		},
		async registerPeppolSelfBilling(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/register-peppol-self-billing`);
		},
		async unregisterPeppolSelfBilling(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/unregister-peppol-self-billing`);
		},
		async deletePeppolEndpoint(dossierId:number, endpoint:string):Promise<void>{
			await Axios.delete(`${this.getMainAppUrl(dossierId)}/remove-from-peppol/${endpoint}`);
		},
		async activatePeppol(companyId:number, dossierId:number):Promise<void>{
			await Axios.post(`${this.getUrl(companyId)}/${dossierId}/activate-peppol`);
		},
		async deleteUnusedProducts(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/delete-unused-products`);
		},
		async createPriceListItemsForAllProductsAndDeleteUnused(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/create-price-list-items-for-all-products-and-delete-unused-products`);
		},
		async createPriceListItemsForAllProducts(dossierId:number):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(dossierId)}/create-price-list-items-for-all-products`);
		},
		async fixSaleActions(dossierId:number, bookyear:number):Promise<{RowsChanged:number}>{
			let result = await Axios.post(`${this.getMainAppUrl(dossierId)}/fix-missing-sale-actions`, {BookYear: bookyear});
			return {RowsChanged: result.data.Changed};
		},
		async quickCopyDossier(sourceDossierId:number, targetDossierId:number, moveUserDataSettings:IMoveUserDataSetting[]):Promise<void>{
			await Axios.post(`${this.getMainAppUrl(sourceDossierId)}/quick-copy-to-test-dossier`, {
				targetDossierId,
				moveUserDataSettings
			},
			{
				timeout: 600e3 // 10 minute timeout cause it's a long task
			});
		}
	}
});
